/* ==========================================================================
   Makefield — carousel
   --------------------------------------------------------------------------
   Prev/next arrows and a progress bar over a native scroll-snap track.

   Binds to any [data-mk-carousel] on the page, so it drives both
   mk-testimonials and mk-logo-carousel. A section declares its own item
   selector with data-mk-item; the progress bar is optional.

   Scrolling is the browser's own (overflow + scroll-snap), so touch swipe,
   trackpad, and keyboard scrolling all work for free and the cards stay in
   the accessibility tree. The arrows just call scrollBy.

   The Fancybox lightbox for the play buttons is bound in mk-feature-rows.js,
   which already binds every [data-fancybox] on the page.
   ========================================================================== */

(function () {
  'use strict';

  function init(root) {
    var track = root.querySelector('[data-mk-track]');
    var prev = root.querySelector('[data-mk-prev]');
    var next = root.querySelector('[data-mk-next]');
    var progress = root.querySelector('[data-mk-progress]');
    if (!track) return;

    /* One scroll step is one item plus the gap. The item selector is a data
       attribute so other sections can reuse this carousel — mk-logo-carousel
       passes its own. Falling back to a page-width scroll keeps the arrows
       useful if a section forgets to declare one. */
    var itemSelector = root.getAttribute('data-mk-item') || '.mk-tst__card';

    function step() {
      var card = track.querySelector(itemSelector);
      if (!card) return track.clientWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    /* Arrows are disabled at the ends rather than hidden, so the control row
       does not reflow and the buttons stay focusable in a stable order.

       When the track does not overflow at all there is nothing to scroll to,
       so the arrows would be permanently dead and the progress bar a full
       width rule that can never move. In that case the controls are hidden
       outright. This is re-evaluated on every sync because a track that fits
       on a wide viewport can still overflow on a narrow one. */
    function sync() {
      var max = maxScroll();
      var x = track.scrollLeft;
      var scrollable = max > 1;

      root.classList.toggle('mk-carousel--static', !scrollable);

      /* hidden hides it from the page and the accessibility tree, and a
         disabled control is skipped by the tab order either way. */
      if (prev) prev.hidden = !scrollable;
      if (next) next.hidden = !scrollable;
      if (progress) {
        var rail = progress.parentElement;
        if (rail) rail.hidden = !scrollable;
      }

      if (!scrollable) return;

      if (prev) prev.disabled = x <= 1;
      if (next) next.disabled = x >= max - 1;

      /* The thumb's width is the visible fraction of the track, and it
         travels across the leftover space as you scroll. */
      if (progress) {
        var visible = track.scrollWidth > 0
          ? track.clientWidth / track.scrollWidth
          : 1;
        var ratio = max > 0 ? x / max : 0;
        var widthPct = Math.min(Math.max(visible * 100, 8), 100);
        progress.style.width = widthPct + '%';
        progress.style.marginInlineStart = ratio * (100 - widthPct) + '%';
      }
    }

    if (prev) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Fonts and lazy images can change the track width after first paint.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);

    sync();
  }

  function initAll() {
    document.querySelectorAll('[data-mk-carousel]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector) {
      event.target.querySelectorAll('[data-mk-carousel]').forEach(init);
    }
  });
})();
